import { DataGrid } from '@mui/x-data-grid'
import {
  Chip,
  Box,
  Typography,
  Button,
  Stack,
  Tooltip,
  IconButton,
  Avatar,
} from '@mui/material'
import moment from 'moment'
import {
  Build,
  Circle,
  Delete,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'

const STATUS_LABEL = {
  active: 'Active',
  corrected: 'Corrected',
  voided: 'Voided',
}
const STATUS_COLOR = {
  active: 'anGreen',
  corrected: 'anOrange',
  voided: 'anRed',
}

const columns = ({
  mfaAuthenticated,
  navigate,
  setOpenMFA,
  onCorrect,
  onVoid,
}) => [
  {
    field: 'createdAt',
    headerName: 'Administered',
    flex: 1.2,
    valueGetter: (value, row) => {
      return row?.administeredTime
        ? moment(row.administeredTime).format('DD MMM YYYY HH:mm')
        : moment(row.updatedAt).format('DD MMM YYYY HH:mm')
    },

    renderCell: ({ row }) => {
      const date = row?.administeredTime ?? row?.updatedAt

      return (
        <Box>
          <Typography fontWeight={600}>
            {moment(date).format('DD MMM YYYY')}
          </Typography>
          <Typography color="text.secondary" fontSize={12}>
            {moment(date).format('HH:mm')}
          </Typography>
        </Box>
      )
    },
  },
  {
    field: 'patient',
    headerName: 'Patient',
    flex: 1.5,
    renderCell: ({ row }) =>
      !mfaAuthenticated ? (
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => setOpenMFA(true)}
        >
          <VisibilityOff fontSize="small" />
          <>Locked</>
        </Stack>
      ) : (
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          gap={1}
          alignItems="center"
        >
          <Box>
            <Typography fontWeight={600}>
              {row?.epcr?.demographics?.firstName ?? row?.patientName ?? ''}{' '}
              {row?.epcr?.demographics?.lastName ?? ''}
            </Typography>
            <Typography color="text.secondary" fontSize={12}>
              DOB:{' '}
              {row?.epcr?.demographics?.dob
                ? moment(row.epcr.demographics.dob).format('DD/MM/YYYY')
                : row?.epcr?.demographics?.dobUnknown &&
                    row?.epcr?.demographics?.dobUnknownButPaediatric
                  ? '<18yrs'
                  : row?.patientDOB
                    ? moment(row.patientDOB).format('DD/MM/YYYY')
                    : 'Unknown'}
            </Typography>
          </Box>
          {row?.epcr?._id && (
            <Box>
              <Button
                onClick={() =>
                  window.open(`/patient-report-forms/${row.epcr._id}`)
                }
                variant="outlined"
                startIcon={<Visibility />}
                size="small"
                color="contrast"
              >
                View ePCR
              </Button>
            </Box>
          )}
        </Stack>
      ),
  },
  {
    field: 'medication',
    headerName: 'Medication',
    flex: 1.5,
    renderCell: ({ row }) => (
      <Box>
        <Typography fontWeight={600}>
          {row?.medicineDescription?.title}
        </Typography>
        <Typography color="text.secondary" fontSize={12}>
          {row?.medicines?.[0]?.batchNumber} | Exp:{' '}
          {moment(row?.medicines?.[0]?.expiry).format('DD/MM/YYYY')}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'recordedBy',
    headerName: 'Administered by',
    flex: 1,
    renderCell: ({ row }) => (
      <Stack direction="row" gap={1} alignItems='flex-start'>
        <Avatar
          src={row?.administeredBy?.photograph}
          sx={{ width: 28, height: 28, borderRadius: 1, mt: 0.5 }}
        />
        <Stack>
          <Stack direction="row">
            <Typography fontWeight={600}>
              {row?.administeredBy?.firstName}
            </Typography>
            <Typography fontWeight={600}>
              {row?.administeredBy?.lastName}
            </Typography>
          </Stack>
          <Typography color="text.secondary" fontSize={12}>
            {row?.administeredBy?.employee?.clinicalRole}
          </Typography>
        </Stack>
      </Stack>
    ),
  },
  {
    field: 'location',
    headerName: 'Location',
    flex:0.7,
    renderCell: ({ row }) => (
      <Box>
        <Typography fontWeight={600}>{row?.medicineLocation?.name}</Typography>
        <Typography color="text.secondary" fontSize={12}>
          {row?.medicineLocation?.type}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'actions',
    headerName: false,
    flex: 0.8,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => {
      const status = row.status || 'active'
      // if ((status || 'active') !== 'active') return null
      const reason =
        status === 'corrected'
          ? row.correctionReason
          : status === 'voided'
            ? row.voidReason
            : ''
      const who =
        status === 'voided'
          ? `${row.voidedBy?.firstName ?? ''} ${row.voidedBy?.lastName ?? ''}`.trim()
          : ''
      const when = status === 'voided' ? row.voidedAt : null
      const tooltip =
        status === 'active'
          ? ''
          : [
              reason && `Reason: ${reason}`,
              who && `By: ${who}`,
              when && `On: ${moment(when).format('DD MMM YYYY HH:mm')}`,
            ]
              .filter(Boolean)
              .join(' · ')

      return (
        <Stack direction="row" gap={1} justifyContent='flex-end' width='100%' alignItems='center'>
          <Tooltip title={tooltip}>
            <Circle color={STATUS_COLOR[status]} size='small'/>

            {/* <Chip
            size="small"
            color={STATUS_COLOR[status]}
            label={STATUS_LABEL[status]}
          /> */}
          </Tooltip>
          <IconButton onClick={() => onCorrect(row)} size='small'>
            <Build color="contrast" />
          </IconButton>
          <IconButton onClick={() => onVoid(row)} size='small'>
            <Delete color="error" />
          </IconButton>
        </Stack>
      )
    },
  },
]

export default columns
